import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from '@reach/router';
import qs from 'query-string';

import isBrowser from '~/utils/is-browser';
import apiCall, { LIVE_URL } from '~/utils/apiCall';
import isEmpty from '~/utils/valid/is-empty';

import SolidBtn from '~/components/atoms/button/solid-button';
import LineBtn from '~/components/atoms/button/line-button';
import NormalInput from '~/components/atoms/input/normal';
import LabelCheckbox from '~/components/atoms/checkbox/label-checkbox';
import LabelRadio from '~/components/atoms/radio/label-radio';
import Textarea from '~/components/atoms/textarea';

import ProBi from './images/pro-bi.svg';
import completeGif from './images/join.gif';

import {
  Wrap,
  ProgressBar,
  QuestionWrap,
  InputWrap, RadioRow, 
  Notice,
  Terms,
  BtnWrap, Img,
} from './styled';

const INIT_USER = {
  email: null,
  name: null,
  phone: null,
  userIdx: null,
}

const INIT_TAKS = {
  // 참여자 정보
  infoQuestion1: '',
  infoQuestion2: '',
  infoQuestion2Etc: '',

  // 대시보드
  dashboardQuestion1: '',
  dashboardQuestion2: '',
  dashboardQuestion3: '',

  // 광고매물관리
  roomManageQuestion1: '',
  roomManageQuestion1Reason: '',
  roomManageQuestion2: '',
  roomManageQuestion2Reason: '',
  roomManageQuestion2ReasonEtc: '',
  roomManageQuestion3: '',
  roomManageQuestion3Etc: '',

  // 광고매물상담
  roomContactQuestion1: '',
  roomContactQuestion1Reason: '',
  roomContactQuestion2: '',
  roomContactQuestion3: '',
  roomContactQuestion4: '',
  roomContactQuestion5: '',
  roomContactQuestion5Sub1: '',
  roomContactQuestion5Sub1Etc: '',
  roomContactQuestion5Sub2: '',

  // 공실센터
  gongsilcenterQuestion1: '',
  gongsilcenterQuestion1NoSub1: '',
  gongsilcenterQuestion1NoSub2: '',
  gongsilcenterQuestion1YesSub1: '',

  // 상품
  productQuestion1: '',
  productQuestion2: '',
  productQuestion3: '',
  productQuestion3Etc: '',

  // 모바일앱
  mobileQuestion1: '',
  mobileQuestion1NoSub1: '',
  mobileQuestion1YesSub1: '',
  mobileQuestion1YesSub2: '',

  // 추가설문
  useQuestion1: '',
  useQuestion2: '',
  useQuestion3: '',
  useQuestion4: '',

  // 타사 서비스 대비
  diffQuestion1: '',
  diffQuestion1Etc: '',
  diffQuestion2: '',
  diffQuestion3: '',
  diffQuestion4: '',
  diffQuestion5: '',
}

function DabangProSurvey() {
  const [agree, setAgree] = useState(false);
  const [step, setStep] = useState('checkUser');
  const [user, setUser] = useState(INIT_USER);
  const [task, setTask] = useState(INIT_TAKS);
  const [sending, setSending] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { pathname, search } = location;
  
  const parse = qs.parse(search);
  const authKey = parse?.auth_key || isBrowser() && sessionStorage.getItem('authKey') || '';
  delete parse.auth_key;

  useEffect(() => {
    navigate(isEmpty(parse) ? pathname : pathname + `?${qs.stringify(parse)}`, { replace: true });

    if (isBrowser()) {
      sessionStorage.clear();
      sessionStorage.setItem('authKey', authKey);
    }
  }, []);

  useEffect(() => {
    if (authKey) getUserInfo();
  }, [authKey]);

  async function getUserInfo() {
    try {
      const res = await apiCall.get(`${LIVE_URL}/api/v1/event/app-survey/pro/target`, { authKey });

      if (res.code === 200) {
        setUser(res.result);

      } else {
        alert(res?.msg ?? '서버에러가 발생했습니다.');
      }

    } catch(err) {
      console.log(err);
    }
  }

  function changeTaskInput(e) {
    const { name, value } = e.currentTarget;

    setTask(prev => ({
      ...prev,
      [name]: value,
    }));
  }

  function changeTaskCheck(e) {
    const { name, value } = e.currentTarget;

    const isEtc = value === '기타';
    const notExist = value === '없음';

    let tempTaskArr = [...task[name]];

    const idx = tempTaskArr.indexOf(value);
    const checked = idx !== -1;

    const etcValue = isEtc && checked ? '' : task[`${name}Etc`];

    if (checked) {
      tempTaskArr.splice(idx, 1);

    } else {
      if (notExist) {
        tempTaskArr = ['없음'];
      } else {
        tempTaskArr.push(value);
      }
    }

    setTask(prev => {
      if (task.hasOwnProperty(`${name}Etc`)) {
        return {
          ...prev,
          [name]: tempTaskArr,
          [`${name}Etc`]: etcValue,
        }
      }

      return {
        ...prev,
        [name]: tempTaskArr,
      }
    });
  }

  async function changeStep(nextStep) {
    const isPrev = nextStep - step < 0;

    if (isPrev) return setStep(nextStep);

    if (nextStep === 1) {
      if (!agree) return alert('개인정보 수집 및 이용동의를 체크 해주세요.');

      try {
        const possible = await apiCall.get(`${LIVE_URL}/api/v1/event/app-survey/pro/possible`);

        if (possible?.code !== 200) {
          return alert(possible?.msg ?? '서버에러가 발생 했습니다.');
        }

      } catch(err) {
        console.log(err);
        return alert('서버에러가 발생했습니다.');
      }

    } else if (nextStep === 2) {
      const valid = [task.infoQuestion1, task.infoQuestion2].every(val => !isEmpty(val));

      if (task.infoQuestion2.includes('기타') && isEmpty(task.infoQuestion2Etc)) return alert("기타 의견을 입력해 주세요.");
      if (!valid) return alert("필수 항목을 입력해 주세요.");

    } else if (nextStep === 3) {
      const valid = [task.dashboardQuestion1, task.dashboardQuestion2, task.dashboardQuestion3].every(val => !isEmpty(val));

      if (!valid) return alert("필수 항목을 입력해 주세요.");

    } else if (nextStep === 4) {
      const valid = [task.roomManageQuestion1, task.roomManageQuestion1Reason, task.roomManageQuestion2, task.roomManageQuestion3].every(val => !isEmpty(val));

      if (task.roomManageQuestion2Reason.includes('기타') && isEmpty(task.roomManageQuestion2ReasonEtc)) return alert("기타 의견을 입력해 주세요.");
      if (task.roomManageQuestion3.includes('기타') && isEmpty(task.roomManageQuestion3Etc)) return alert("기타 의견을 입력해 주세요.");

      if (!valid || task.roomManageQuestion2 === '아니오' && isEmpty(task.roomManageQuestion2Reason)) return alert("필수 항목을 입력해 주세요.");

    } else if (nextStep === 5) {
      const valid = [task.roomContactQuestion1, task.roomContactQuestion1Reason, task.roomContactQuestion2, task.roomContactQuestion3, task.roomContactQuestion4, task.roomContactQuestion5].every(val => !isEmpty(val));

      if (task.roomContactQuestion5Sub1.includes('기타') && isEmpty(task.roomContactQuestion5Sub1Etc)) return alert("기타 의견을 입력해 주세요.");
      if (!valid || (task.roomContactQuestion5 === '예' && (isEmpty(task.roomContactQuestion5Sub1) || isEmpty(task.roomContactQuestion5Sub2)))) return alert("필수 항목을 입력해 주세요.");

    } else if (nextStep === 6) {
      const valid = [task.gongsilcenterQuestion1].every(val => !isEmpty(val));

      if (task.gongsilcenterQuestion1 === '예' && isEmpty(task.gongsilcenterQuestion1YesSub1)) return alert("필수 항목을 입력해 주세요.");
      if (task.gongsilcenterQuestion1 === '아니오' && (isEmpty(task.gongsilcenterQuestion1NoSub1) || isEmpty(task.gongsilcenterQuestion1NoSub2))) return alert("필수 항목을 입력해 주세요.");
      if (!valid) return alert("필수 항목을 입력해 주세요.");

    } else if (nextStep === 7) {
      const valid = [task.productQuestion1, task.productQuestion2, task.productQuestion3].every(val => !isEmpty(val));

      if (task.productQuestion3.includes('기타') && isEmpty(task.productQuestion3Etc)) return alert("기타 의견을 입력해 주세요.");
      if (!valid) return alert("필수 항목을 입력해 주세요.");

    } else if (nextStep === 8) {
      if (isEmpty(task.mobileQuestion1)) return alert("필수 항목을 입력해 주세요.");
      if (task.mobileQuestion1 === '예' && (isEmpty(task.mobileQuestion1YesSub1) || isEmpty(task.mobileQuestion1YesSub2))) return alert("필수 항목을 입력해 주세요.");
      if (task.mobileQuestion1 === '아니오' && isEmpty(task.mobileQuestion1NoSub1)) return alert("필수 항목을 입력해 주세요.");

    } else if (nextStep === 9) {
      const valid = [task.useQuestion1, task.useQuestion2, task.useQuestion3, task.useQuestion4].every(val => !isEmpty(val));

      if (!valid) return alert("필수 항목을 입력해 주세요.");

    } else if (nextStep === 'submit') {
      return submit();
    }

    setStep(nextStep);
  }

  async function submit() {
    if (sending) return;

    if (!window.confirm('설문을 제출 하시겠습니까?')) return;

    setSending(true);

    try {
      let tempTask = {};

      for (let [key, value] of Object.entries(task)) {
        const isArr = Array.isArray(value);

        if (isArr) {
          const idx = value.findIndex(v => v === '기타');
      
          if (idx !== -1) {
            value[idx] = `기타_${task[`${key}Etc`]}`
          }
      
        } else {
          if (value === '기타') {
            value = `기타_${task[`${key}Etc`]}`
          }
        }
      
        tempTask[key] = isEmpty(value) ? null : value;
      }

      const res = await apiCall.post(`${LIVE_URL}/api/v1/event/app-survey/pro/submit`, {
        userIdx: user.userIdx,
        ...tempTask,
      });

      const { code } = res;

      if (code === 200) {
        setStep('complete');

      } else {
        alert(res?.msg ?? '서버에러가 발생했습니다.');
      }

      setSending(false);

    } catch(err) {
      console.log(err);
      setSending(false);
    }
  }

  return (
    <Wrap>
      <div>
        <img src={ProBi} alt="다방로고" style={{ marginBottom: 32 }} />

        {[1, 2, 3, 4, 5, 6, 7, 8, 9].includes(step) && (
          <ProgressBar step={step} />
        )}

        {step === 'checkUser' ? (
          <>
            <InputWrap>
              <p>이름</p>

              <NormalInput
                value={user?.name ?? ''}
                readOnly 
              />
            </InputWrap>
  
            <InputWrap>
              <p>이메일</p>

              <NormalInput
                value={user?.email ?? ''}
                readOnly 
              />
            </InputWrap>
  
            <InputWrap>
              <p>연락처</p>

              <NormalInput
                value={user?.phone ?? ''}
                readOnly 
              />
            </InputWrap>
  
            <Notice style={{ margin: '20px 0' }}>위 정보가 맞는지 확인후 설문을 시작해주세요!</Notice>
  
            <LabelCheckbox
              disabled={isEmpty(user?.userIdx) ?? false}
              onChange={() => setAgree(prev => !prev)}
            >
              개인정보 수집 및 이용동의
            </LabelCheckbox>
  
            <BtnWrap>
              <SolidBtn
                disabled={!agree}
                onClick={() => changeStep(1)}
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
              >
                다음
              </SolidBtn>
            </BtnWrap>
          </>
        ) : step === 1 ? (
          <>
            <h1 style={{ lineHeight: '32px', marginBottom: 32 }}>TASK1. 참여자 정보</h1>

            <QuestionWrap>
              <p>Q. 다방프로 서비스를 이용한 기간을 선택 해주세요.<span>*</span></p>

              {['1년', '2년', '3년', '4년', '5년 이상'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="infoQuestion1"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.infoQuestion1.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 다방프로 이외 이용중인 타사 서비스가 있나요?<span>*</span></p>

              {['직방', '네이버', '피터팬', '부동산써브', '매일경제', '부동산114', '기타'].map(q => (
                <RadioRow key={q}>
                  <LabelCheckbox
                    name="infoQuestion2"
                    value={q}
                    onChange={changeTaskCheck}
                    checked={task.infoQuestion2.includes(q)}
                  >
                    {q}
                  </LabelCheckbox>
                </RadioRow>
              ))}

              {task.infoQuestion2.includes('기타') && (
                <InputWrap>
                  <p>기타의견을 작성해 주세요.</p>

                  <NormalInput
                    name="infoQuestion2Etc"
                    value={task.infoQuestion2Etc}
                    onChange={changeTaskInput}
                  />
                </InputWrap>
              )}
            </QuestionWrap>

            <BtnWrap>
              <SolidBtn
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
                onClick={changeStep.bind(null, 2)}
              >
                다음
              </SolidBtn>
            </BtnWrap>
          </>
        ) : step === 2 ? (
          <>
            <h1 style={{ lineHeight: '32px', marginBottom: 32 }}>TASK2. 대시보드</h1>

            <QuestionWrap>
              <p>Q. 대시보드에서 가장 유용하게 사용하는 항목은 무엇인가요?<span>*</span></p>

              {['매물등록현황', '상담현황', '조회현황', '상품보유현황', '공실센터', '공지사항 및 뉴스', '없음'].map(q => (
                <RadioRow key={q}>
                  <LabelCheckbox
                    name="dashboardQuestion1"
                    value={q}
                    disabled={task.dashboardQuestion1.includes('없음') && q !== '없음'}
                    onChange={changeTaskCheck}
                    checked={task.dashboardQuestion1.includes(q)}
                  >
                    {q}
                  </LabelCheckbox>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 대시보드에서 불필요하다고 생각하는 항목은 무엇인가요?<span>*</span></p>

              {['매물등록현황', '상담현황', '조회현황', '상품보유현황', '공실센터', '공지사항 및 뉴스', '없음'].map(q => (
                <RadioRow key={q}>
                  <LabelCheckbox
                    name="dashboardQuestion2"
                    value={q}
                    disabled={task.dashboardQuestion2.includes('없음') && q !== '없음'}
                    onChange={changeTaskCheck}
                    checked={task.dashboardQuestion2.includes(q)}
                  >
                    {q}
                  </LabelCheckbox>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 대시보드에 추가되었으면 하는 항목이 있으신가요?<span>*</span></p>

              <Textarea
                name="dashboardQuestion3"
                value={task.dashboardQuestion3}
                onChange={changeTaskInput}
              />
            </QuestionWrap>

            <BtnWrap>
              <LineBtn
                contentWidth="120px"
                btnColor="gray"
                btnSize="xl"
                onClick={changeStep.bind(null, 1)}
              >
                이전
              </LineBtn>
  
              <SolidBtn
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
                onClick={changeStep.bind(null, 3)}
              >
                다음
              </SolidBtn>
            </BtnWrap>
          </>
        ) : step === 3 ? (
          <>
            <h1 style={{ lineHeight: '32px', marginBottom: 32 }}>TASK3. 광고 매물관리</h1>

            <QuestionWrap>
              <p>Q. 매물 등록시 가장 확인하기 어려운 항목은 무엇인가요?<span>*</span></p>

              {['매물유형', '건축물주용도', '건축물승인일자', '건물크기(공급/전용)', '건물층수', '방수', '욕실수', '난방종류', '건물방향', '베란다/발코니 여부', '빌트인여부', '엘리베이터여부', '주차여부', '관리비 또는 개별사용료', '추가옵션', '사진', '없음'].map(q => (
                <RadioRow key={q}>
                  <LabelCheckbox
                    name="roomManageQuestion1"
                    value={q}
                    disabled={task.roomManageQuestion1.includes('없음') && q !== '없음'}
                    onChange={changeTaskCheck}
                    checked={task.roomManageQuestion1.includes(q)}
                  >
                    {q}
                  </LabelCheckbox>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 위와 같이 답변한 사유는 무엇인가요?<span>*</span></p>

              <Textarea
                name="roomManageQuestion1Reason"
                value={task.roomManageQuestion1Reason}
                onChange={changeTaskInput}
              />
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 매물 등록시 확인매물 상품을 적용하신 경험이 있으신가요?<span>*</span></p>

              {['예', '아니오'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="roomManageQuestion2"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.roomManageQuestion2.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            {task.roomManageQuestion2 === '아니오' && (
              <QuestionWrap>
                <p>Q. 확인매물상품을 사용 경험이 없다면 사유는 무엇인가요?<span>*</span></p>

                {['임대인 정보 확인이 어려움', '광고효과 부족', '비용이 비싸서', '주변 중개사들도 사용하지 않음', '기타'].map(q => (
                  <RadioRow key={q}>
                    <LabelCheckbox
                      name="roomManageQuestion2Reason"
                      value={q}
                      onChange={changeTaskCheck}
                      checked={task.roomManageQuestion2Reason.includes(q)}
                    >
                      {q}
                    </LabelCheckbox>
                  </RadioRow>
                ))}

                {task.roomManageQuestion2Reason.includes('기타') && (
                  <InputWrap>
                    <p>기타의견을 작성해 주세요.</p>

                    <NormalInput
                      name="roomManageQuestion2ReasonEtc"
                      value={task.roomManageQuestion2ReasonEtc}
                      onChange={changeTaskInput}
                    />
                  </InputWrap>
                )}
              </QuestionWrap>
            )}

            <QuestionWrap>
              <p>Q. 매물관리에서 등록한 매물을 찾으려고할때 어떤 방식을 선호하나요?<span>*</span></p>

              {['매물번호', '주소', '매물유형', '방수', '거래유형', '상품유형', '추가필터', '기타'].map(q => (
                <RadioRow key={q}>
                  <LabelCheckbox
                    name="roomManageQuestion3"
                    value={q}
                    onChange={changeTaskCheck}
                    checked={task.roomManageQuestion3.includes(q)}
                  >
                    {q}
                  </LabelCheckbox>
                </RadioRow>
              ))}

              {task.roomManageQuestion3.includes('기타') && (
                <InputWrap>
                  <p>기타의견을 작성해 주세요.</p>

                  <NormalInput
                    name="roomManageQuestion3Etc"
                    value={task.roomManageQuestion3Etc}
                    onChange={changeTaskInput}
                  />
                </InputWrap>
              )}
            </QuestionWrap>

            <BtnWrap>
              <LineBtn
                contentWidth="120px"
                btnColor="gray"
                btnSize="xl"
                onClick={changeStep.bind(null, 2)}
              >
                이전
              </LineBtn>
  
              <SolidBtn
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
                onClick={changeStep.bind(null, 4)}
              >
                다음
              </SolidBtn>
            </BtnWrap>
          </>
        ) : step === 4 ? (
          <>
            <h1 style={{ lineHeight: '32px', marginBottom: 32 }}>TASK4. 매물상담</h1>

            <QuestionWrap>
              <p>Q. 다방에서 제공하는 문의방식 중 가장 편한 방식이 무엇인가요?<span>*</span></p>

              {['전화문의', '문자문의', '간편문의', '없음'].map(q => (
                <RadioRow key={q}>
                  <LabelCheckbox
                    name="roomContactQuestion1"
                    value={q}
                    onChange={changeTaskCheck}
                    disabled={task.roomContactQuestion1.includes('없음') && q !== '없음'}
                    checked={task.roomContactQuestion1.includes(q)}
                  >
                    {q}
                  </LabelCheckbox>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 위와 같이 답변한 사유는 무엇인가요?<span>*</span></p>

              <Textarea
                name="roomContactQuestion1Reason"
                value={task.roomContactQuestion1Reason}
                onChange={changeTaskInput}
              />
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 다방에서 매물 보고 문의한 이용자(고객)와 상담할 때, 상담에 가장 불편한 사항이 무엇인지 말씀 해 주세요.<span>*</span></p>

              <Textarea
                name="roomContactQuestion2"
                value={task.roomContactQuestion2}
                onChange={changeTaskInput}
              />
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 이용자(고객) 간편문의 알림톡은 오전9시~오후9시까지 발송 해 드리고 있습니다. 이용시간에 대한 의견을 말씀 해 주세요.<span>*</span></p>

              {['현재 정책 좋다.', '시간 설정을 개인마다 다르게 할 수 있었으면 좋겠다.', '시간 제한 두지 않고. 이용자 문의는 실시간 받고 싶다.'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="roomContactQuestion3"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.roomContactQuestion3.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 간편문의 OR 문자문의로 문의한 이용자(고객)에게 전화 통화 시도시 실제 통화 연결이 잘 되는지 말씀 해 주세요.<span>*</span></p>

              {['거의 연결 된다.', '50% 정도 연결 되는 편이다.', '거의 연결 되지 않는다.', '연결 된 적 없다.'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="roomContactQuestion4"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.roomContactQuestion4.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 간편문의 OR 문자문의에 대해 특정 사유로 인하여 답변을 하지 못한 경험이 있는지 말씀 해 주세요.<span>*</span></p>

              {['예', '아니오'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="roomContactQuestion5"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.roomContactQuestion5.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            {task.roomContactQuestion5 === '예' && (
              <>
                <QuestionWrap>
                  <p>Q. 답변을 잊은 경험이 있는 경우 사유는 무엇인가요?<span>*</span></p>

                  {['다른 고객을 응대하는 중이라서', '문의 사실 자체를 잊음', '휴무날이라 신경쓰지 못함', '의도적으로 답변하지 않음', '기타'].map(q => (
                    <RadioRow key={q}>
                      <LabelRadio
                        name="roomContactQuestion5Sub1"
                        value={q}
                        onChange={changeTaskInput}
                        checked={task.roomContactQuestion5Sub1.includes(q)}
                      >
                        {q}
                      </LabelRadio>
                    </RadioRow>
                  ))}
                </QuestionWrap>

                {task.roomContactQuestion5Sub1.includes('기타') && (
                  <InputWrap>
                    <p>기타의견을 작성해 주세요.</p>

                    <NormalInput
                      name="roomContactQuestion5Sub1Etc"
                      value={task.roomContactQuestion5Sub1Etc}
                      onChange={changeTaskInput}
                    />
                  </InputWrap>
                )}

                <QuestionWrap>
                  <p>Q. 답변을 잊은 경우 추가 또는 주기적으로 알림톡을 보내 드리면 좋을지 말씀 해 주세요.<span>*</span></p>

                  {['제공 받았으면 좋겠다.', '추가 알림톡은 필요없다.'].map(q => (
                    <RadioRow key={q}>
                      <LabelRadio
                        name="roomContactQuestion5Sub2"
                        value={q}
                        onChange={changeTaskInput}
                        checked={task.roomContactQuestion5Sub2.includes(q)}
                      >
                        {q}
                      </LabelRadio>
                    </RadioRow>
                  ))}
                </QuestionWrap>
              </>
            )}

            <BtnWrap>
              <LineBtn
                contentWidth="120px"
                btnColor="gray"
                btnSize="xl"
                onClick={changeStep.bind(null, 3)}
              >
                이전
              </LineBtn>
  
              <SolidBtn
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
                onClick={changeStep.bind(null, 5)}
              >
                다음
              </SolidBtn>
            </BtnWrap>
          </>
        ) : step === 5 ? (
          <>
            <h1 style={{ lineHeight: '32px', marginBottom: 32 }}>TASK5. 공실센터</h1>

            <QuestionWrap>
              <p>Q. 공실센터 서비스를 사용한적이 있으신가요?<span>*</span></p>

              {['예', '아니오'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="gongsilcenterQuestion1"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.gongsilcenterQuestion1.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            {task.gongsilcenterQuestion1 === '아니오' ? (
              <>
                <QuestionWrap>
                  <p>Q. 공실센터 서비스를 사용한적이 없다면 사유는 무엇인가요?<span>*</span></p>

                  {['매물이 없어서', '서비스를 모른다', '본인인증이 필요해서'].map(q => (
                    <RadioRow key={q}>
                      <LabelRadio
                        name="gongsilcenterQuestion1NoSub1"
                        value={q}
                        onChange={changeTaskInput}
                        checked={task.gongsilcenterQuestion1NoSub1.includes(q)}
                      >
                        {q}
                      </LabelRadio>
                    </RadioRow>
                  ))}
                </QuestionWrap>

                <QuestionWrap>
                  <p>Q. 다방에서 더욱 많은 공실매물을 제공한다면 사용할 의사가 있으신가요?<span>*</span></p>

                  {['예', '아니오'].map(q => (
                    <RadioRow key={q}>
                      <LabelRadio
                        name="gongsilcenterQuestion1NoSub2"
                        value={q}
                        onChange={changeTaskInput}
                        checked={task.gongsilcenterQuestion1NoSub2.includes(q)}
                      >
                        {q}
                      </LabelRadio>
                    </RadioRow>
                  ))}
                </QuestionWrap>
              </>
            ) : task.gongsilcenterQuestion1 === '예' ? (
              <QuestionWrap>
                <p>Q. 공실센터 서비스를 사용한적이 있다면 공실센터 서비스의 장점은 무엇이라고 생각하시나요?<span>*</span></p>

                <Textarea
                  name="gongsilcenterQuestion1YesSub1"
                  value={task.gongsilcenterQuestion1YesSub1}
                  onChange={changeTaskInput}
                />
              </QuestionWrap>
            ) : null}

            <BtnWrap>
              <LineBtn
                contentWidth="120px"
                btnColor="gray"
                btnSize="xl"
                onClick={changeStep.bind(null, 4)}
              >
                이전
              </LineBtn>
  
              <SolidBtn
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
                onClick={changeStep.bind(null, 6)}
              >
                다음
              </SolidBtn>
            </BtnWrap>
          </>
        ) : step === 6 ? (
          <>
            <h1 style={{ lineHeight: '32px', marginBottom: 32 }}>TASK6. 상품</h1>

            <QuestionWrap>
              <p>Q. 상품을 구매 또는 결제할때 불편하거나 개선해야 하는 부분이 있다면 무엇인가요?<span>*</span></p>

              <Textarea
                name="productQuestion1"
                value={task.productQuestion1}
                onChange={changeTaskInput}
              />
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 다방에서 '매물촬영', '매물등록'을 대행 해 주는 상품이 있다면, 구매 의향이 있으신지 말씀 해 주세요.<span>*</span></p>

              {['예', '아니오'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="productQuestion2"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.productQuestion2.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 중개계약서 관리를 위해 사용하는 프로그램을 말씀 해 주세요.<span>*</span></p>

              {['한방', '거래정보망(레이다,텐 등)', '워드*한글파일', '기타'].map(q => (
                <RadioRow key={q}>
                  <LabelCheckbox
                    name="productQuestion3"
                    value={q}
                    onChange={changeTaskCheck}
                    checked={task.productQuestion3.includes(q)}
                  >
                    {q}
                  </LabelCheckbox>
                </RadioRow>
              ))}

              {task.productQuestion3.includes('기타') && (
                <InputWrap>
                  <p>기타 프로그램을 입력 해주세요.</p>

                  <NormalInput
                    name="productQuestion3Etc"
                    value={task.productQuestion3Etc}
                    onChange={changeTaskInput}
                  />
                </InputWrap>
              )}
            </QuestionWrap>

            <BtnWrap>
              <LineBtn
                contentWidth="120px"
                btnColor="gray"
                btnSize="xl"
                onClick={changeStep.bind(null, 5)}
              >
                이전
              </LineBtn>

              <SolidBtn
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
                onClick={changeStep.bind(null, 7)}
              >
                다음
              </SolidBtn>
            </BtnWrap>
          </>
        ) : step === 7 ? (
          <>
            <h1 style={{ lineHeight: '32px', marginBottom: 32 }}>TASK7. 모바일앱</h1>

            <QuestionWrap>
              <p>Q. 모바일앱을 사용한적이 있으신가요?<span>*</span></p>

              {['예', '아니오'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="mobileQuestion1"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.mobileQuestion1.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            {task.mobileQuestion1 === '아니오' ? (
              <QuestionWrap>
                <p>Q. 모바일앱을 사용하지 않는 이유는 무엇인가요?<span>*</span></p>

                <Textarea
                  name="mobileQuestion1NoSub1"
                  value={task.mobileQuestion1NoSub1}
                  onChange={changeTaskInput}
                />
              </QuestionWrap>
            ) : task.mobileQuestion1 === '예' ? (
              <>
                <QuestionWrap>
                  <p>Q. 모바입앱의 장점은 무엇이라고 생각하시나요?<span>*</span></p>

                  <Textarea
                    name="mobileQuestion1YesSub1"
                    value={task.mobileQuestion1YesSub1}
                    onChange={changeTaskInput}
                  />
                </QuestionWrap>
                
                <QuestionWrap>
                  <p>Q. 모바일앱에서 개선 또는 추가되었으면 하는 기능은 무엇이라고 생각하시나요?<span>*</span></p>

                  <Textarea
                    name="mobileQuestion1YesSub2"
                    value={task.mobileQuestion1YesSub2}
                    onChange={changeTaskInput}
                  />
                </QuestionWrap>
              </>
            ) : null}

            <BtnWrap>
              <LineBtn
                contentWidth="120px"
                btnColor="gray"
                btnSize="xl"
                onClick={changeStep.bind(null, 6)}
              >
                이전
              </LineBtn>

              <SolidBtn
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
                onClick={changeStep.bind(null, 8)}
              >
                다음
              </SolidBtn>
            </BtnWrap>
          </>
        ) : step === 8 ? (
          <>
            <h1 style={{ lineHeight: '32px', marginBottom: 32 }}>TASK8. 추가설문항목</h1>

            <QuestionWrap>
              <p>Q. 한달 기준 평균 결제금액은?<span>*</span></p>

              <Textarea
                name="useQuestion1"
                value={task.useQuestion1}
                onChange={changeTaskInput}
              />
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 평균적으로 몇개의 매물을 등록 및 광고하시나요?<span>*</span></p>

              {['1~10개', '11~20개', '21~30개', '31~40개', '41~50개', '51개이상'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="useQuestion2"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.useQuestion2.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 평균적으로 1주 기준 몇번의 문의가 있나요?<span>*</span></p>

              <Textarea
                name="useQuestion3"
                value={task.useQuestion3}
                onChange={changeTaskInput}
              />
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 평균적으로 1주 기준 몇건의 계약이 발생하나요?<span>*</span></p>

              <Textarea
                name="useQuestion4"
                value={task.useQuestion4}
                onChange={changeTaskInput}
              />
            </QuestionWrap>

            <BtnWrap>
              <LineBtn
                contentWidth="120px"
                btnColor="gray"
                btnSize="xl"
                onClick={changeStep.bind(null, 7)}
              >
                이전
              </LineBtn>

              <SolidBtn
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
                onClick={changeStep.bind(null, 9)}
              >
                다음
              </SolidBtn>
            </BtnWrap>
          </>
        ) : step === 9 ? (
          <>
            <h1 style={{ lineHeight: '32px', marginBottom: 32 }}>TASK9. 편의성</h1>

            <QuestionWrap>
              <p>Q. 다방 이외 사용하고 계신 타사 서비스는 무엇인가요?<span>*</span></p>

              {['직방', '피터팬', '써브(네이버)', '매경(네이버)', '부동산뱅크(네이버)', '네이버부동산', '기타'].map(q => (
                <RadioRow key={q}>
                  <LabelCheckbox
                    name="diffQuestion1"
                    value={q}
                    onChange={changeTaskCheck}
                    checked={task.diffQuestion1.includes(q)}
                  >
                    {q}
                  </LabelCheckbox>
                </RadioRow>
              ))}

              {task.diffQuestion1.includes('기타') && (
                <InputWrap>
                  <p>기타의견을 작성해 주세요.</p>

                  <NormalInput
                    name="diffQuestion1Etc"
                    value={task.diffQuestion1Etc}
                    onChange={changeTaskInput}
                  />
                </InputWrap>
              )}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 타사 서비스 대비 매물관리 기능의 편의성 점수는?<span>*</span></p>

              {['5점', '4점', '3점', '2점', '1점'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="diffQuestion2"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.diffQuestion2.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 타사 서비스 대비 계정관리 기능의 편의성 점수는?<span>*</span></p>

              {['5점', '4점', '3점', '2점', '1점'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="diffQuestion3"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.diffQuestion3.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 타사 서비스 대비 문의확인 기능의 편의성 점수는?<span>*</span></p>

              {['5점', '4점', '3점', '2점', '1점'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="diffQuestion4"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.diffQuestion4.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            <QuestionWrap>
              <p>Q. 타사 서비스 대비 광고효과 점수는?<span>*</span></p>

              {['5점', '4점', '3점', '2점', '1점'].map(q => (
                <RadioRow key={q}>
                  <LabelRadio
                    name="diffQuestion5"
                    value={q}
                    onChange={changeTaskInput}
                    checked={task.diffQuestion5.includes(q)}
                  >
                    {q}
                  </LabelRadio>
                </RadioRow>
              ))}
            </QuestionWrap>

            <BtnWrap>
              <LineBtn
                contentWidth="120px"
                btnColor="gray"
                btnSize="xl"
                onClick={changeStep.bind(null, 8)}
              >
                이전
              </LineBtn>

              <SolidBtn
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
                onClick={changeStep.bind(null, 'submit')}
              >
                제출
              </SolidBtn>
            </BtnWrap>
          </>
        ) : (
          <>
            <Img
              src={completeGif}
              width="60"
              height="60"
              alt="매물 사진"
              draggable={false}
              loading="lazy"
            />

            <h1 style={{ textAlign: 'center', fontSize: 16, lineHeight: '32px' }}>설문에 참여 해주셔서 감사합니다.</h1>

            <BtnWrap>
              <SolidBtn
                contentWidth="120px"
                btnColor="blue"
                btnSize="xl"
                onClick={() => window.open('','_self').close()}
              >
                닫기
              </SolidBtn>
            </BtnWrap>
          </>
        )}
      </div>
    </Wrap>
  );
}

export default DabangProSurvey;
